import { AdductionOrder } from '@features/siteStatusV2/types';
import { OrderStepStatus } from '@features/siteStatusV2/types/order/enums';
import theme, { ThemeVariants } from '@theme';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';
import { DisplayFieldValue } from '../display-form';
import StepsModal from './StepsModal';

interface AdductionStepProps {
  order: AdductionOrder;
}

const AdductionStep: React.FC<AdductionStepProps> = ({ order }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const [selectedOrder, setSelectedOrder] = useState<AdductionOrder | null>(null);

  const handleSeeDetailsBn = useCallback(() => {
    setSelectedOrder(order);
  }, [order]);

  const getOrderStepStatus = useCallback(
    (state?: OrderStepStatus) => {
      const statusMap: Record<OrderStepStatus, { color: ThemeVariants; label: string }> = {
        [OrderStepStatus.FINISHED]: {
          color: 'blue',
          label: t('orderDetails.badges.status.finished')
        },
        [OrderStepStatus.IN_PROGRESS]: {
          color: 'green',
          label: t('orderDetails.badges.status.inProgress')
        },
        [OrderStepStatus.COMING_SOON]: {
          color: 'grey',
          label: t('orderDetails.badges.status.comingSoon')
        }
      };

      return statusMap[state ?? OrderStepStatus.COMING_SOON];
    },
    [t]
  );

  const stepLabels = useMemo(
    () => ({
      fieldVisit: t('orderDetails.adduction.siteVisit'),
      quote: t('orderDetails.adduction.clientQuote'),
      works: t('orderDetails.adduction.construction'),
      orderCompleted: t('orderDetails.adduction.orderClosure')
    }),
    []
  );

  const getLastFinishedOrInProgressStep = useCallback(() => {
    const steps = {
      fieldVisit: order.fieldVisit,
      quote: order.quote,
      works: order.works,
      orderCompleted: order.orderCompleted
    };

    return (
      Object.entries(steps)
        .filter(
          ([_, step]) =>
            step?.stepStatus === OrderStepStatus.FINISHED ||
            step?.stepStatus === OrderStepStatus.IN_PROGRESS
        )
        .pop() || null
    );
  }, [order]);

  const lastStep = getLastFinishedOrInProgressStep();
  const { color, label } = useMemo(
    () => getOrderStepStatus(lastStep?.[1]?.stepStatus),
    [lastStep, getOrderStepStatus]
  );

  return (
    <>
      <Step>
        <div className="d-flex align-items-center">
          <LabelStep>{t('orderDetails.adduction.step')}:</LabelStep>
          <ValueStep>{stepLabels[lastStep?.[0] as keyof typeof stepLabels] || '-'}</ValueStep>
          <DisplayFieldValue label={label} badge={{ color, full: true }} />
        </div>
        <SeeDetailsBtn type="button" onClick={handleSeeDetailsBn}>
          {t('orderDetails.adduction.seeDetails')}
        </SeeDetailsBtn>
      </Step>
      <StepsModal order={selectedOrder} setSelectedOrder={setSelectedOrder} />
    </>
  );
};

const Step = styled.div`
  display: flex;
  align-items: center;
  border: solid 1px ${theme.palette.blue[200]};
  padding: 0.5rem 0.75rem;
  background-color: ${theme.palette.blue[50]};
  border-radius: 4px;
  justify-content: space-between;

  > div {
    display: flex;
    flex-grow: 1;
  }
`;

const LabelStep = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${theme.palette.blue[800]};
`;

const ValueStep = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${theme.palette.grey[700]};
  margin: 0 0.5rem;
`;

const SeeDetailsBtn = styled.button`
  font-size: ${theme.fonts.size.xs};
  font-weight: ${theme.fonts.weight.medium};
  color: ${theme.palette.grey[50]};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background: ${theme.palette.blue[500]};
  border: none;
`;

export default AdductionStep;
